import { CreateOrganization } from "@clerk/react";
import {
  Autocomplete,
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { DefinitionRow } from "@/components/core/definition-row";
import { ErrorDisplay } from "@/components/core/error-display";
import { IntegrationStatus } from "@/components/core/integration-status";
import { CardLoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link, LinkButton } from "@/components/core/tanstack-link";
import { formatDate } from "@/helpers/intl";
import {
  getGetAdminOrganizationsQueryKey,
  getGetSubtenantsQueryKey,
  getStatusQueryKey,
  type SubOrganization,
  TenantOption,
  useAddSubtenants,
  useGetAdminOrganizations,
  useGetSubtenants,
  useUnlinkOrganizations,
} from "@/models/backend";
import z from "zod";
import { enqueueSnackbar } from "notistack";
import { onError } from "@/components/core/display-error";

const search = z.object({
  showAdd: z.boolean().optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/admin/organizations")({
  component: RouteComponent,
  validateSearch: search,
});

const OrganizationCard = ({
  organization,
  isMain,
}: {
  organization: SubOrganization;
  isMain: boolean;
}) => {
  const [confirmDeleteOrg, setConfirmDeleteOrg] = useState(false);
  const { mutateAsync: unlinkOrganization, isPending: isRemovingThis } = useUnlinkOrganizations();
  const queryClient = useQueryClient();
  const onRemoveOrganization = async () => {
    if (!organization) return;
    setConfirmDeleteOrg(false);
    try {
      await unlinkOrganization({ params: { child_id: organization.id } });
      await queryClient.invalidateQueries({
        queryKey: getGetAdminOrganizationsQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getStatusQueryKey(),
      });
      enqueueSnackbar("Organization deleted successfully", { variant: "success" });
    } catch (e) {
      onError({ prefix: "There was an error deleting organization" })(e);
    }
  };
  const onClickRemoveOrganization = () => {
    setConfirmDeleteOrg(true);
  };

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <CardHeader
          title={organization.name}
          action={
            organization.logo ? (
              <img
                alt=""
                src={organization.logo}
                style={{ maxHeight: "50px", maxWidth: "150px" }}
              />
            ) : (
              <Avatar />
            )
          }
        />
        <CardContent>
          {organization.integrations.map((integration) => (
            <IntegrationStatus key={integration.id} integration={integration} />
          ))}
          {organization.integrations.length === 0 && (
            <Chip
              label="Not connected"
              color="warning"
              variant="outlined"
              avatar={<Avatar sx={{ bgcolor: "warning.main" }}> </Avatar>}
            />
          )}
          <TableContainer sx={{ marginTop: 2 }}>
            <Table size="small" sx={{ td: { borderBottom: 0 }, marginLeft: -1 }}>
              <TableBody>
                <DefinitionRow
                  label="Default sender"
                  value={organization.default_sender?.email ?? "N/A"}
                />
                <DefinitionRow
                  label="Next sending date"
                  value={formatDate(organization.next_run)}
                />
                <DefinitionRow
                  label="Workflows"
                  value={
                    <Link to="/workflows" search={{ organizationId: organization.external_id }}>
                      {organization.workflow_count} workflows
                    </Link>
                  }
                />
                <DefinitionRow
                  label="Customers monitored"
                  value={
                    <Link to={"/customers"} search={{ organizationId: organization.external_id }}>
                      {organization.customers} customers
                    </Link>
                  }
                />
                <DefinitionRow
                  label="Emails in next 2 weeks"
                  value={
                    <Link
                      to={"/events"}
                      search={{
                        organizationId: organization.external_id,
                        tab: "outbox",
                      }}
                    >
                      {organization.pending} emails
                    </Link>
                  }
                />
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
        <CardActions>
          {!isMain && (
            <Button
              sx={{ marginRight: "auto" }}
              variant="outlined"
              color="warning"
              onClick={onClickRemoveOrganization}
              disabled={isRemovingThis}
              startIcon={isRemovingThis ? <CircularProgress size={20} /> : undefined}
            >
              Delete
            </Button>
          )}
          <LinkButton
            to="/dashboard"
            search={{ organizationId: organization.external_id }}
            variant="contained"
          >
            Go to organisation
          </LinkButton>
        </CardActions>
      </Card>
      <Dialog open={confirmDeleteOrg} onClose={() => setConfirmDeleteOrg(false)}>
        <DialogTitle>Delete organisation</DialogTitle>
        <DialogContent>This will delete {organization?.name} completely. Continue?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOrg(false)}>Cancel</Button>
          <Button onClick={() => onRemoveOrganization()} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const EmptyCard = ({ onAddOrganization }: { onAddOrganization: () => void }) => {
  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader title="Add new organisation" action={<Avatar />} />
      <CardContent sx={{ flexGrow: 1 }} />
      <CardActions>
        <Button variant="contained" onClick={onAddOrganization}>
          Set up organisation
        </Button>
      </CardActions>
    </Card>
  );
};

const AddTenants = ({ onClose }: { onClose: () => void }) => {
  const { data: subtenants, isLoading } = useGetSubtenants();
  const { mutate: addSubtenants, isPending } = useAddSubtenants();
  const [selectedTenants, setSelectedTenants] = useState<TenantOption[]>(
    subtenants?.filter((tenant) => tenant.exists) ?? [],
  );

  const queryClient = useQueryClient();

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    addSubtenants(
      { data: selectedTenants },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: getGetAdminOrganizationsQueryKey() });
          await queryClient.invalidateQueries({ queryKey: getGetSubtenantsQueryKey() });
          enqueueSnackbar("Tenants added successfully", { variant: "success" });
          onClose();
        },
        onError: onError({
          prefix: "Couldn't add tenants",
          andThen: () => {
            queryClient.invalidateQueries({ queryKey: getGetAdminOrganizationsQueryKey() });
          },
        }),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Choose which organisation(s) to connect</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            You can add more organisations at any point from the admin dashboard.
          </Typography>
          <Autocomplete
            loading={isLoading}
            options={subtenants ?? []}
            renderInput={(params) => <TextField {...params} value />}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.name}
            size="small"
            multiple
            sx={{ minWidth: "250px" }}
            value={selectedTenants}
            onChange={(e, value) => setSelectedTenants(value ?? [])}
            renderOption={(props, option) => {
              if (!("number" in option)) {
                return (
                  <ListItem {...props} key={option.id}>
                    {option.name}
                  </ListItem>
                );
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} sx={{ mr: "auto" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={20} /> : null}
        >
          Confirm
        </Button>
      </DialogActions>
    </form>
  );
};

function RouteComponent() {
  const { data: organization, isLoading, error, isError, refetch } = useGetAdminOrganizations();
  const { showAdd } = Route.useSearch();
  const navigate = Route.useNavigate();
  const isMultitenant = organization?.sub_organizations.some((org) => org.is_multitenant);

  if (isError) {
    return (
      <Stack spacing={4}>
        <Typography variant="h3">Organisations</Typography>{" "}
        <ErrorDisplay title="Couldn't load your organisations" error={error} onRetry={refetch} />
      </Stack>
    );
  }
  if (isLoading || !organization) {
    return <CardLoadingSkeleton />;
  }
  return (
    <Stack spacing={4}>
      <Typography variant="h3">Organisations</Typography>
      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        {organization?.sub_organizations.map((subOrganization) => (
          <Grid key={subOrganization.id} size={{ xs: 12, sm: 6 }}>
            <OrganizationCard
              organization={subOrganization}
              isMain={subOrganization.id === organization.id}
            />
          </Grid>
        ))}
        <Grid key={"new"} size={{ xs: 12, sm: 6 }}>
          <EmptyCard onAddOrganization={() => navigate({ search: { showAdd: true } })} />
        </Grid>
      </Grid>
      {showAdd && (
        <Dialog open={showAdd} onClose={() => navigate({ search: { showAdd: false } })}>
          {isMultitenant ? (
            <AddTenants onClose={() => navigate({ search: { showAdd: false } })} />
          ) : (
            <DialogContent>
              <CreateOrganization
                skipInvitationScreen={true}
                afterCreateOrganizationUrl={`/admin/organizations?link=${organization.id}`}
              />
            </DialogContent>
          )}
        </Dialog>
      )}
    </Stack>
  );
}
